import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocaleFmt } from "@/lib/i18n/format";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { adminListAllTickets, getTicket, replyTicket, adminUpdateTicketStatus } from "@/lib/support.functions";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, LifeBuoy, Search } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/support")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: AdminSupportPage,
});

type Row = { id: string; owner_id: string; subject: string; category: string; priority: string; status: string; created_at: string; last_reply_at: string | null };

function AdminSupportPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const list = useServerFn(adminListAllTickets);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const r: any = await list();
    setRows(Array.isArray(r) ? r : []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) =>
    (status === "all" || r.status === status) &&
    (!q || r.subject.toLowerCase().includes(q.toLowerCase()))
  );

  if (openId) return <AdminTicketDetail id={openId} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <>
      <PageHeader
        title={t("pages.admin.supportPage.title")}
        description={t("pages.admin.supportPage.description")}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm"><Link to="/app/admin"><ArrowLeft className="mr-2 h-4 w-4" />{t("pages.admin.title")}</Link></Button>
            <Badge variant="secondary" className="gap-1"><LifeBuoy className="h-3 w-3" /> {t("pages.admin.supportPage.totalTickets", { n: rows.length })}</Badge>
          </div>
        }
      />
      <PageBody>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-3">
              <span>{t("pages.admin.supportPage.ticketsTitle")}</span>
              <div className="flex items-center gap-2">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.admin.supportPage.all")}</SelectItem>
                    <SelectItem value="open">{t("pages.admin.supportPage.open")}</SelectItem>
                    <SelectItem value="answered">{t("pages.admin.supportPage.answered")}</SelectItem>
                    <SelectItem value="resolved">{t("pages.admin.supportPage.resolved")}</SelectItem>
                    <SelectItem value="closed">{t("pages.admin.supportPage.closed")}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pages.admin.search")} className="pl-8" />
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-sm text-muted-foreground">{t("pages.admin.loading")}</p> : (
              <div className="space-y-2">
                {filtered.map((tk) => (
                  <button key={tk.id} onClick={() => setOpenId(tk.id)} className="block w-full rounded-lg border border-border bg-card p-3 text-left hover:bg-secondary/30">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{tk.subject}</div>
                        <div className="text-xs text-muted-foreground">{tk.category} · {tk.priority} · {t("pages.admin.supportPage.userPrefix")} {tk.owner_id.slice(0, 8)}… · {new Date(tk.created_at).toLocaleString(localeTag)}</div>
                      </div>
                      <Badge variant={tk.status === "open" ? "default" : "secondary"}>{tk.status}</Badge>
                    </div>
                  </button>
                ))}
                {filtered.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">{t("pages.admin.supportPage.noTickets")}</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}

function AdminTicketDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const get = useServerFn(getTicket);
  const reply = useServerFn(replyTicket);
  const updateStatus = useServerFn(adminUpdateTicketStatus);
  const [data, setData] = useState<any>(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { const r: any = await get({ data: { id } }); setData(r); };
  useEffect(() => { load(); }, [id]);

  const send = async () => {
    if (!content.trim()) return;
    setBusy(true);
    const r: any = await reply({ data: { ticketId: id, content } });
    if (!r?.ok) toast.error(r?.error ?? t("pages.admin.genericError"));
    else { setContent(""); await load(); toast.success(t("pages.admin.supportPage.replySent")); }
    setBusy(false);
  };

  const setStatus = async (status: string) => {
    const r: any = await updateStatus({ data: { id, status: status as any } });
    if (r?.ok) { toast.success(t("pages.admin.supportPage.statusUpdated")); load(); }
  };

  if (!data?.ticket) return <PageBody><p className="text-sm text-muted-foreground">{t("pages.admin.loading")}</p></PageBody>;

  return (
    <>
      <PageHeader title={data.ticket.subject} description={t("pages.admin.supportPage.ticketHeader", { user: data.ticket.owner_id.slice(0, 8) + "…", date: new Date(data.ticket.created_at).toLocaleString(localeTag) })} actions={
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" />{t("pages.admin.supportPage.backToList")}</Button>
          <Select value={data.ticket.status} onValueChange={setStatus}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">{t("pages.admin.supportPage.statusOpen")}</SelectItem>
              <SelectItem value="answered">{t("pages.admin.supportPage.statusAnswered")}</SelectItem>
              <SelectItem value="resolved">{t("pages.admin.supportPage.statusResolved")}</SelectItem>
              <SelectItem value="closed">{t("pages.admin.supportPage.statusClosed")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      } />
      <PageBody>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {(data.messages ?? []).map((m: any) => (
                <div key={m.id} className={m.role === "admin" ? "ml-auto max-w-[80%] rounded-xl bg-primary/15 px-3 py-2 text-sm" : "mr-auto max-w-[90%] rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm"}>
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{m.role === "admin" ? t("pages.admin.supportPage.meSupport") : m.role === "bot" ? t("pages.admin.supportPage.bot") : t("pages.admin.supportPage.userRole")} · {new Date(m.created_at).toLocaleString(localeTag)}</div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t("pages.admin.supportPage.replyPh")} rows={4} />
              <div className="flex justify-end">
                <Button onClick={send} disabled={!content.trim() || busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pages.admin.supportPage.send")}</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}
